import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
}

const SECTIONS: Section[] = [
  { id: 'overview',       title: 'Overview' },
  { id: 'adding-members', title: 'Adding Members' },
  { id: 'managing-member', title: 'Managing a Member' },
  { id: 'licenses',       title: 'License Information' },
  { id: 'cycles',         title: 'Compliance Cycles' },
  { id: 'firm-session',   title: 'Firm Sessions (Bulk)' },
  { id: 'team-admins',    title: 'Team Admins' },
  { id: 'compliance',     title: 'Compliance Reports' },
];

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <div className="d-flex gap-3 mb-3">
    <div
      className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold"
      style={{ width: 28, height: 28, fontSize: '0.8rem', marginTop: 2 }}
    >
      {n}
    </div>
    <div className="pt-1 small">{children}</div>
  </div>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="alert alert-info py-2 px-3 small mb-3">{children}</div>
);

const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="alert alert-success py-2 px-3 small mb-3">💡 {children}</div>
);

const TeamHelpGuide: React.FC = () => {
  const [activeId, setActiveId] = useState('overview');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        }
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const ref = (id: string) => (el: HTMLElement | null) => { sectionRefs.current[id] = el; };

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h3 className="fw-bold mb-1">Team Admin Guide</h3>
          <p className="text-muted mb-0 small">Everything you need to manage your firm's CE compliance on CEC Track.</p>
        </div>
        <Link to="/team" className="btn btn-outline-secondary btn-sm">← Team Dashboard</Link>
      </div>

      <div className="row g-4">
        {/* Sticky sidebar TOC */}
        <div className="col-lg-3 d-none d-lg-block">
          <div className="sticky-top" style={{ top: '1.5rem' }}>
            <p className="text-uppercase fw-semibold small text-muted mb-2" style={{ letterSpacing: '0.05em' }}>
              Contents
            </p>
            <nav className="nav flex-column gap-1">
              {SECTIONS.map(({ id, title }) => (
                <button
                  key={id}
                  className="btn btn-link text-start p-0 text-decoration-none"
                  style={{
                    fontSize: '0.85rem',
                    color: activeId === id ? '#0d6efd' : '#6c757d',
                    fontWeight: activeId === id ? 600 : 400,
                  }}
                  onClick={() => scrollTo(id)}
                >
                  {title}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="col-lg-9">

          {/* ── Overview ── */}
          <section id="overview" ref={ref('overview')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Overview</h4>
            <p className="small">
              A <strong>Team Account</strong> lets a firm or organization centrally manage continuing education
              compliance for multiple license holders. Instead of each professional tracking their own credits
              in isolation, a team admin can add members, set up their licenses and cycles, assign classes, and
              review compliance — all from one place.
            </p>

            <h6 className="fw-semibold mt-4 mb-3">Role Hierarchy</h6>
            <div className="table-responsive">
              <table className="table table-bordered table-sm small">
                <thead className="table-light">
                  <tr>
                    <th>Role</th>
                    <th>Who</th>
                    <th>Can Do</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="badge bg-primary">Owner</span></td>
                    <td>The account holder who subscribed to the Team plan</td>
                    <td>Everything — including billing, adding/removing team admins</td>
                  </tr>
                  <tr>
                    <td><span className="badge bg-info text-dark">Team Admin</span></td>
                    <td>Any CEC Track user the owner designates as admin</td>
                    <td>Add/remove members, manage licenses &amp; cycles, firm sessions</td>
                  </tr>
                  <tr>
                    <td><span className="badge bg-light text-dark border">Member</span></td>
                    <td>Any licensed professional added to the firm</td>
                    <td>Their own dashboard — can log classes, view compliance</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h6 className="fw-semibold mt-4 mb-3">Member Types</h6>
            <div className="row g-3">
              <div className="col-sm-6">
                <div className="card h-100 border-info">
                  <div className="card-body p-3">
                    <span className="badge bg-info text-dark mb-2">Linked</span>
                    <p className="small mb-0">
                      The member has (or will create) their own CEC Track login. They can log into their
                      account independently and see all their own data. The firm can view their compliance
                      and manage cycles/classes on their behalf.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="card h-100 border-secondary">
                  <div className="card-body p-3">
                    <span className="badge bg-secondary mb-2">Managed</span>
                    <p className="small mb-0">
                      The firm fully controls this profile. The member may or may not have login access
                      (they set a password via an invite email). Useful for staff who don't manage their
                      own CE tracking.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Adding Members ── */}
          <section id="adding-members" ref={ref('adding-members')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Adding Members</h4>

            <p className="small mb-4">
              From the <Link to="/team">Team Dashboard</Link>, click <strong>+ Add Member</strong>.
              You'll choose between two modes:
            </p>

            <h6 className="fw-semibold mb-3">Option A — Invite by Email</h6>
            <p className="small">Use this when the person either already has a CEC Track account or will create one themselves.</p>
            <Step n={1}>Click <strong>+ Add Member</strong> → select <strong>Invite by Email</strong>.</Step>
            <Step n={2}>Enter their email address and click <strong>Add Member</strong>.</Step>
            <Step n={3}>
              <strong>If they already have an account:</strong> they are added instantly as a linked member.
              <br />
              <strong>If they don't:</strong> an invite email is sent. They'll appear as <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>Invite Pending</span> until they register.
            </Step>
            <Step n={4}>Once they register via the invite link, they can log in to their own account. Their profession is set during registration.</Step>
            <Tip>If an invite expires or wasn't received, click <strong>Resend</strong> on their row to generate a new link.</Tip>

            <hr className="my-4" />

            <h6 className="fw-semibold mb-3">Option B — Create Managed Profile</h6>
            <p className="small">Use this for staff members where the firm manages all their CE data.</p>
            <Step n={1}>Click <strong>+ Add Member</strong> → select <strong>Create Managed Profile</strong>.</Step>
            <Step n={2}>Enter their first name, last name, and email address, then click <strong>Create Profile</strong>.</Step>
            <Step n={3}>A <em>Set Password</em> email is sent to them. They click the link to set a password and can then log in.</Step>
            <Step n={4}>Until they set their password, they appear as <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>Invite Pending</span>. You can still manage their profile in the meantime.</Step>
            <Note>Managed members can only belong to one firm. They cannot be added to another company's team.</Note>
          </section>

          {/* ── Managing a Member ── */}
          <section id="managing-member" ref={ref('managing-member')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Managing a Member</h4>
            <p className="small mb-3">
              Click <strong>Manage</strong> on any managed member's row in the Team Dashboard to open their
              individual management page (<code>/team/members/:id</code>). Linked members can be viewed via
              their <strong>Report</strong> link.
            </p>
            <p className="small">The member management page contains:</p>
            <ul className="small">
              <li><strong>Member info</strong> — email, primary profession, licenses on file</li>
              <li><strong>Licenses</strong> — add, edit, or remove their license records</li>
              <li><strong>Compliance Cycles</strong> — add or delete renewal cycles</li>
              <li><strong>Quick links</strong> — jump to their compliance report or open a firm session</li>
            </ul>
            <Note>
              Changes you make here (licenses, cycles) appear immediately in the member's own account
              when they log in.
            </Note>
          </section>

          {/* ── Licenses ── */}
          <section id="licenses" ref={ref('licenses')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">License Information</h4>
            <p className="small mb-3">
              Licenses tell the system <em>which profession</em> and <em>which state</em> a person holds
              a license in. This is required to look up the correct CE requirements and generate
              compliance reports.
            </p>

            <h6 className="fw-semibold mb-3">Adding a License</h6>
            <Step n={1}>Open the member's management page via <strong>Manage</strong> on the Team Dashboard.</Step>
            <Step n={2}>In the <strong>Licenses</strong> card, click <strong>+ Add License</strong>.</Step>
            <Step n={3}>Select the <strong>Profession</strong> (e.g. Architect, Engineer, CPA) and <strong>State</strong>.</Step>
            <Step n={4}>Enter the <strong>License Number</strong>, optionally the issue year, and set the <strong>Status</strong> to Active.</Step>
            <Step n={5}>Click <strong>Add License</strong>. The license appears in the table immediately.</Step>
            <Tip>
              If the member has no profession set yet, adding their first license will automatically set
              their primary profession to match.
            </Tip>

            <h6 className="fw-semibold mb-3 mt-4">Editing or Removing a License</h6>
            <Step n={1}>Click <strong>Edit</strong> on any license row.</Step>
            <Step n={2}>Update any fields and click <strong>Save Changes</strong>, or click <strong>Delete</strong> to remove it.</Step>
            <Note>State cannot be changed after a license is created — delete and re-add if the state needs to change.</Note>
          </section>

          {/* ── Cycles ── */}
          <section id="cycles" ref={ref('cycles')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Compliance Cycles</h4>
            <p className="small mb-3">
              A <strong>Cycle</strong> is one renewal period for a specific profession + state license
              (e.g. NY Architecture 2024–2026). Classes are attached to cycles to track CE credit progress.
            </p>

            <h6 className="fw-semibold mb-3">Adding a Cycle for a Member</h6>
            <Step n={1}>Open the member's management page and scroll to <strong>Compliance Cycles</strong>.</Step>
            <Step n={2}>Click <strong>+ Add Cycle</strong>.</Step>
            <Step n={3}>Enter a <strong>Cycle Name</strong> (e.g. "NY Architecture 2024–2026").</Step>
            <Step n={4}>
              Select <strong>Profession</strong> and <strong>State</strong>. If a matching state
              requirement exists, the <strong>End Date</strong> will auto-fill based on the renewal
              period, and a blue info bar shows the credit requirement.
            </Step>
            <Step n={5}>Confirm or adjust the <strong>Start</strong> and <strong>End</strong> dates, then click <strong>Create Cycle</strong>.</Step>
            <Tip>
              Some states (e.g. NY Architecture) have fixed cycle dates that are preset automatically.
              You can still override them if the member's actual renewal window differs.
            </Tip>

            <h6 className="fw-semibold mb-3 mt-4">Viewing or Adding Classes to a Cycle</h6>
            <p className="small">
              Click the cycle name in the table to open the full <strong>Cycle Detail</strong> page.
              From there you can attach or remove classes and see compliance progress by category.
            </p>

            <h6 className="fw-semibold mb-3 mt-4">Deleting a Cycle</h6>
            <p className="small">
              Click <strong>Delete</strong> on any cycle row. This detaches all classes from the cycle
              but does <em>not</em> delete the class records themselves.
            </p>
          </section>

          {/* ── Firm Sessions ── */}
          <section id="firm-session" ref={ref('firm-session')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Firm Sessions (Bulk Class Assignment)</h4>
            <p className="small mb-3">
              A <strong>Firm Session</strong> lets you record one class (e.g. an in-house training or
              webinar) and assign it to multiple team members at once — each getting their own class
              record in their own cycles.
            </p>

            <h6 className="fw-semibold mb-3">How to Add a Firm Session</h6>
            <Step n={1}>
              From the <Link to="/team">Team Dashboard</Link>, click <strong>+ Add Firm Session</strong>
              (or from any member's management page).
            </Step>
            <Step n={2}>
              <strong>Step 1 — Class Details:</strong> Enter the class name, date, credit hours,
              provider, location, and any categories. Toggle <em>Lecture/Live</em> vs <em>Self-Study</em>.
            </Step>
            <Step n={3}>
              <strong>Step 2 — Assign Members:</strong> Check the members who attended.
              For each selected member, pick the cycle(s) to assign the class to.
              You can also override the categories per member if they differ.
            </Step>
            <Step n={4}>
              <strong>Step 3 — Review &amp; Submit:</strong> Confirm the assignments and click
              <strong> Submit</strong>. One class record is created per member.
            </Step>
            <Note>
              Only members with at least one active cycle will appear in Step 2. Add cycles first
              if a member is missing from the list.
            </Note>
            <Tip>
              Firm sessions are ideal for recurring in-house trainings. Run it once and all
              attendees get their credit automatically.
            </Tip>
          </section>

          {/* ── Team Admins ── */}
          <section id="team-admins" ref={ref('team-admins')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Team Admins</h4>
            <p className="small mb-3">
              As the account <strong>Owner</strong>, you can delegate management to other CEC Track
              users by adding them as <strong>Team Admins</strong>. Team admins have the same
              management capabilities as the owner, except they cannot modify billing or add/remove
              other admins.
            </p>

            <h6 className="fw-semibold mb-3">Adding a Team Admin</h6>
            <Step n={1}>On the <Link to="/team">Team Dashboard</Link>, find the <strong>Team Admins</strong> card (visible to owner only).</Step>
            <Step n={2}>Click <strong>+ Add Admin</strong>.</Step>
            <Step n={3}>Enter the email address of an existing CEC Track user and click <strong>Add Admin</strong>.</Step>
            <Step n={4}>They immediately gain access to the Team Dashboard and all management pages. The <strong>Team</strong> link appears in their navbar.</Step>

            <h6 className="fw-semibold mb-3 mt-4">Removing a Team Admin</h6>
            <p className="small">
              Click <strong>Remove</strong> on any admin row. Their access is revoked immediately.
              Their own CEC Track account and any member data they managed are not affected.
            </p>
            <Note>Team admins must already have a CEC Track account. You cannot invite someone to be an admin — have them register first, then add them.</Note>
          </section>

          {/* ── Compliance Reports ── */}
          <section id="compliance" ref={ref('compliance')} className="mb-5">
            <h4 className="fw-bold border-bottom pb-2 mb-4">Compliance Reports</h4>
            <p className="small mb-3">
              CEC Track generates a compliance report for each member showing credits earned vs.
              required across all active cycles, broken down by category.
            </p>

            <h6 className="fw-semibold mb-3">Viewing a Member's Compliance Report</h6>
            <Step n={1}>On the Team Dashboard, click <strong>Report</strong> on any accepted member's row.</Step>
            <Step n={2}>The report shows each active cycle with total credits earned, per-category progress, and any compliance gaps.</Step>
            <Tip>
              You can also access the report from the member's management page via the
              <strong> View Compliance Report</strong> button.
            </Tip>

            <h6 className="fw-semibold mb-3 mt-4">What Counts Toward Compliance</h6>
            <ul className="small">
              <li>Only classes marked as <strong>Completed</strong> (took = true) count toward credit totals.</li>
              <li>A class must be attached to the correct cycle to count.</li>
              <li>Category requirements (e.g. Ethics, HSW) must be met in addition to the total credit requirement.</li>
              <li>Per-cycle category assignments (set on the Cycle Detail page) override the class's default categories for that cycle's compliance calculation.</li>
            </ul>
          </section>

          {/* Footer */}
          <div className="text-center text-muted small mt-4 pt-4 border-top">
            Questions? Contact us at{' '}
            <a href="mailto:support@cectrack.com">support@cectrack.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamHelpGuide;
